import { redirect } from 'react-router';
import { storefrontContext } from '~/domain/core/index.server';
import { resolveCheckoutExperience } from '../checkout-loader-functions';
import { buildCheckoutUrl } from '~/domain/connect/deep-link';
import {
  isShared,
  resolveConnectedStorefront,
} from '~/domain/connect/index.server';
import { env } from '~/platform/platform.server';
import { loggerContext } from '~/lib/logger';
import { notFound } from '~/lib/react-router';
import type { Route } from './+types/upcoming-events-route';
import { continueBooking, loadBookingDialogDeferred } from './load.server';
import { parseSelectionFromForm } from './parse-selection.server';
import { BookingDialogRoute } from './booking-dialog-route';
import { closeToPath } from './upcoming-events-params';

/**
 * The booking dialog on the upcoming-events page.
 *
 * Unlike the experience page's dialog, this page is not experience-scoped —
 * it lists events across every experience — so the experience arrives as
 * `?experienceId` rather than a path segment. That keeps the page's own URL
 * (and its filters) intact underneath the dialog.
 */
const UUID_REGEX = /^[0-9a-f-]{1,64}$/i;

function requireExperienceId(url: URL): string {
  const experienceId = url.searchParams.get('experienceId');
  if (!experienceId || !UUID_REGEX.test(experienceId)) throw notFound();
  return experienceId;
}

export function loader({ request, url, context, params }: Route.LoaderArgs) {
  const experienceId = requireExperienceId(url);

  // Nothing is awaited: sync fields open the dialog (with a live date
  // stepper) instantly, while the `tickets` and `events` promises stream in
  // behind per-section loading states (see BookingDialogRoute).
  //
  // `loadBookingDialogDeferred` takes `params` as a plain object, so threading
  // the id in from the query needs no change on its side.
  return {
    ...loadBookingDialogDeferred({
      request,
      url,
      context,
      params: { ...params, experienceId },
    }),
    closeTo: closeToPath(url),
  };
}

export async function action({
  request,
  url,
  context,
  params,
}: Route.ActionArgs) {
  const experienceId = requireExperienceId(url);
  const logger = context.get(loggerContext);
  const checkoutExperience = await resolveCheckoutExperience({
    params: { ...params, experienceId },
    storefront: context.get(storefrontContext),
    logger,
  });
  const { experience } = checkoutExperience;

  // Shared (connected) experiences are hosted by another company, so checkout
  // must happen on the owner's storefront — same hand-off as the experience
  // page's dialog, which is why the form parser is shared between them.
  if (isShared(experience)) {
    const owner = await resolveConnectedStorefront(experience.hostId, {
      experienceId: experience.ownerExperienceId,
      logger,
    });
    const target = buildCheckoutUrl({
      experience,
      ownerFqdn: owner?.fqdn ?? null,
      locale: params.locale,
      currentUrl: url,
      rootDomain: env.ROOT_DOMAIN ?? '',
      selection: await parseSelectionFromForm(request, checkoutExperience),
    });
    return redirect(target);
  }

  return continueBooking(
    { request, url, params },
    experienceId,
    checkoutExperience
  );
}

export default function UpcomingEventsBookingRoute({
  loaderData,
}: Route.ComponentProps) {
  const { closeTo, ...dialogData } = loaderData;
  return <BookingDialogRoute loaderData={dialogData} closeTo={closeTo} />;
}
